/**
 * Module canonique contact info — **aucun** import depuis `app/...` :
 * le fichier est consommé par appInbe via `@community/survey-info-contact` ;
 * les chemins `app/*` n’y sont pas résolus.
 *
 * Les littéraux correspondent à `ViewerTypeEnum` (NAME → infoName, etc.).
 */
export type SurveyDefInfoConstraintsInput = {
  questions?: Record<string, { viewerType?: string; type?: string } | undefined>
  structureElements?: {
    questionsGroups?: {
      properties?: Record<string, { subType?: string } | undefined>
      questionsIndex?: Record<string, string[] | undefined>
    }
  }
}

/** Clés question canoniques (v1), alignées sur l’ordre des contacts. */
export const CONTACT_INFO_CANONICAL_QUESTION_KEYS = [
  'q_info_name',
  'q_info_address',
  'q_info_email',
  'q_info_phone',
] as const

export type ContactInfoCanonicalQuestionKey = (typeof CONTACT_INFO_CANONICAL_QUESTION_KEYS)[number]

/** Les quatre types de contact « info » (unicité + exclusivité modes). */
export const CONTACT_INFO_VIEWER_TYPES = [
  'infoName',
  'infoAddress',
  'infoEmail',
  'infoPhone',
] as const

export type ContactInfoViewerType = (typeof CONTACT_INFO_VIEWER_TYPES)[number]

/** Ordre d’affichage UI (palette, listes). */
export const CONTACT_INFO_VIEWER_ORDER: readonly ContactInfoViewerType[] = [
  'infoName',
  'infoAddress',
  'infoEmail',
  'infoPhone',
]

export function isContactInfoViewerType(v: string | undefined | null): v is ContactInfoViewerType {
  return !!v && (CONTACT_INFO_VIEWER_TYPES as readonly string[]).includes(v)
}

const DUPLICATE_PREFIX = 'duplicate_contact_info_viewerType:'
const EXCLUSIVE_VIOLATION = 'info_modes_exclusive_violation'

/**
 * Valide unicité des quatre contacts info et exclusivité groupe `infos` vs questions contact hors groupe `infos`.
 * Pure : aucune dépendance Angular ni chemins `app/`.
 */
export function validateSurveyInfoConstraints(surveyDef: SurveyDefInfoConstraintsInput): { ok: boolean; errors: string[] } {
  const errors: string[] = []
  const questions = surveyDef?.questions || {}
  const qgProps = surveyDef?.structureElements?.questionsGroups?.properties || {}
  const questionsIndex = surveyDef?.structureElements?.questionsGroups?.questionsIndex || {}

  let hasInfosGroup = false
  for (const gKey of Object.keys(qgProps)) {
    if (qgProps[gKey]?.subType === 'infos') {
      hasInfosGroup = true
      break
    }
  }

  const seenViewerTypes = new Set<string>()
  let hasStandaloneContactInfo = false

  for (const gKey of Object.keys(questionsIndex)) {
    const groupSubType = qgProps[gKey]?.subType
    const inInfosGroup = groupSubType === 'infos'
    const keys = questionsIndex[gKey] || []
    for (const qKey of keys) {
      const q = questions[qKey]
      if (!q?.viewerType || !isContactInfoViewerType(q.viewerType)) {
        continue
      }
      /** Mode « individuel » : contact info hors groupe `infos` (cf. plan produit). */
      if (!inInfosGroup && q.type === 'info') {
        hasStandaloneContactInfo = true
      }
      if (seenViewerTypes.has(q.viewerType)) {
        errors.push(`${DUPLICATE_PREFIX}${q.viewerType}`)
      } else {
        seenViewerTypes.add(q.viewerType)
      }
    }
  }

  if (hasInfosGroup && hasStandaloneContactInfo) {
    errors.push(EXCLUSIVE_VIOLATION)
  }

  return { ok: errors.length === 0, errors: [...new Set(errors)] }
}
